import { useEffect, useMemo, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { SortableColumnPicker } from "@/components/dataTable";
import type { GeschaeftspartnerTyp } from "@/lib/kontakte";
import {
  GESCHAEFTSPARTNER_REQUIRED_COLUMNS,
  getAvailableGeschaeftspartnerColumns,
  getDefaultGeschaeftspartnerColumns,
  getGeschaeftspartnerColumnLabel,
  normalizeGeschaeftspartnerColumnSelection,
} from "@/pages/kontakte/geschaeftspartnerColumns";
import type { GeschaeftspartnerColumnSettings } from "@/pages/kontakte/useGeschaeftspartnerTableSettings";

export interface GeschaeftspartnerColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  typ: GeschaeftspartnerTyp;
  settings: GeschaeftspartnerColumnSettings;
  onSave: (next: GeschaeftspartnerColumnSettings) => void;
}

export function GeschaeftspartnerColumnsDialog({
  open,
  onClose,
  typ,
  settings,
  onSave,
}: GeschaeftspartnerColumnsDialogProps) {
  const available = useMemo(() => getAvailableGeschaeftspartnerColumns(typ), [typ]);
  const defaults = useMemo(() => getDefaultGeschaeftspartnerColumns(typ), [typ]);
  const [selected, setSelected] = useState<string[]>(defaults);
  const [baseline, setBaseline] = useState<string[]>(defaults);

  useEffect(() => {
    if (!open) return;
    const next = settings.columns?.length
      ? normalizeGeschaeftspartnerColumnSelection(settings.columns, typ)
      : defaults;
    setSelected(next);
    setBaseline(next);
  }, [open, settings, typ, defaults]);

  function handleSave() {
    onSave({
      columns: normalizeGeschaeftspartnerColumnSelection(selected, typ),
      widths: settings.widths ?? {},
    });
    onClose();
  }

  const dirty = useMemo(() => hasFormChanges(selected, baseline), [selected, baseline]);
  const requestClose = useDialogRequestClose(onClose);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Spalten anpassen"
      description="Wähle die Spalten für die Tabelle und ziehe sie per Drag-and-Drop in die gewünschte Reihenfolge."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => setSelected(defaults)}>
            Standard wiederherstellen
          </Button>
          <Button variant="secondary" onClick={() => setSelected(available)}>
            Alle auswählen
          </Button>
          <Button variant="ghost" onClick={requestClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>Speichern</Button>
        </>
      }
    >
      <SortableColumnPicker
        available={available}
        selected={selected}
        requiredColumns={GESCHAEFTSPARTNER_REQUIRED_COLUMNS}
        getLabel={getGeschaeftspartnerColumnLabel}
        onSelectedChange={setSelected}
      />
    </Dialog>
  );
}
